import { useState } from 'react';
import { Box } from '@mui/material';
import { PageTitle } from '../../components/PageTitle';
import { AnimatedBox } from '../../components/AnimatedBox';
import { EmptyState } from '../../components/EmptyState';
import { LoadingState } from '../../components/LoadingState';
import type { Notification } from '../../type';
import { AddNotificationButton } from '../../components/AddNotificationButton';
import { NotificationTable } from '../../components/NotificationTable';
import { CreateNotificationDialog } from '../../components/CreateNotificationDialog';
import { NotificationDetailDialog } from '../../components/NotificationDetailDialog';

interface NotificationContentProps {
  notifications: Notification[];
  loading: boolean;
  error: string | null;
  addNotification: (notification: Notification) => Promise<void>;
  deleteNotification: (notificationId: string) => Promise<void>;
}

const NotificationContent = ({
  notifications,
  loading,
  error,
  addNotification,
  deleteNotification,
}: NotificationContentProps) => {
  const [open, setOpen] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState<Notification | null>(null);

  const handleSaveNotification = async (notification: Notification) => {
    try {
      await addNotification(notification);
      setOpen(false);
    } catch (error) {
      alert('알림 생성에 실패했습니다.');
    }
  };

  const handleDeleteNotification = async (notificationId?: string) => {
    if (!notificationId) return;
    try {
      const confirmed = window.confirm('정말로 이 알림을 삭제하시겠습니까?');
      if (confirmed) {
        await deleteNotification(notificationId);
        setSelectedNotification(null);
      }
    } catch (error) {
      alert('알림 삭제에 실패했습니다.');
    }
  };

  if (loading) {
    return (
      <AnimatedBox>
        <PageTitle>알림 관리</PageTitle>
        <LoadingState />
      </AnimatedBox>
    );
  }

  return (
    <AnimatedBox>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'row', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        mb: 3 
      }}>
        <PageTitle>알림 관리</PageTitle>
        <AddNotificationButton onAddClick={() => setOpen(true)} />
      </Box>

      {error && (
        <Box sx={{ mb: 3, color: 'error.main' }}>
          {error}
        </Box>
      )}

      {notifications.length === 0 ? (
        <EmptyState message="아직 등록된 알림이 없습니다." />
      ) : (
        <NotificationTable 
          data={notifications} 
          onNotificationSelect={setSelectedNotification}
        />
      )}

      <CreateNotificationDialog 
        open={open} 
        onClose={() => setOpen(false)} 
        onSave={handleSaveNotification} 
      />

      <NotificationDetailDialog 
        notification={selectedNotification} 
        onClose={() => setSelectedNotification(null)}
        handleDelete={handleDeleteNotification}
      />
    </AnimatedBox>
  );
};

export default NotificationContent; 